import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import { RequireAuth } from '../lib/auth';

import ProtectedRoute from '../lib/auth/ProtectedRoute';
import SuperadminRoute from '../lib/auth/SuperadminRoute';
import { ROLES, ACCESS_FLAGS } from '../lib/auth/roles';
import NotAuthorized from '../pages/NotAuthorized';
import LegacyHostRedirect from './LegacyHostRedirect';

const DashboardDefault = Loadable(() => import('pages/dashboard/default'));
const Color = Loadable(() => import('pages/component-overview/color'));
const Typography = Loadable(() => import('pages/component-overview/typography'));
const Shadow = Loadable(() => import('pages/component-overview/shadows'));
const SamplePage = Loadable(() => import('pages/extra-pages/sample-page'));
const ReportMenu = Loadable(() => import('pages/reports/reportMenu'));
const NightSummary = Loadable(() => import('pages/reports/nightSummary'));
const PublicRsvpReport = Loadable(() => import('pages/reports/publicRsvp'));
const TicketSalesReport = Loadable(() => import('pages/reports/ticketSales'));
const MyReports = Loadable(() => import('pages/reports/myReport'));
const AllReports = Loadable(() => import('pages/reports/all'));
const HostPhotoAttendanceReport = Loadable(() => import('pages/reports/hostPhotoAttendance'));
const TeamReports = Loadable(() => import('pages/reports/teamReport'));
const TeamBirthday = Loadable(() => import('pages/reports/teamBirthday'));
const CurrentTeam = Loadable(() => import('pages/reports/currentTeam'));
const Bday = Loadable(() => import('pages/clubastra/bday'));
const Booths = Loadable(() => import('pages/clubastra/booths'));
const Perks = Loadable(() => import('pages/clubastra/perks'));
const Pays = Loadable(() => import('pages/clubastra/pays'));
const HostMissions = Loadable(() => import('pages/clubastra/missions'));
const Playbook = Loadable(() => import('pages/clubastra/playbook'));
const PlaysGallery = Loadable(() => import('pages/clubastra/playsGallery'));
const AddGuestlist = Loadable(() => import('pages/clubastra/addGuestlist'));
const FreeEntry = Loadable(() => import('pages/clubastra/freeentry'));
const ManagementFreeList = Loadable(() => import('pages/clubastra/managementFreeList'));
const AddDinner = Loadable(() => import('pages/clubastra/addDinner'));
const AddDinnerManagement = Loadable(() => import('pages/clubastra/addDinnerManagement'));
const Content = Loadable(() => import('pages/clubastra/content'));
const RequestHost = Loadable(() => import('pages/clubastra/requestHost'));
const ManagementRequestHost = Loadable(() => import('pages/clubastra/managementRequestHost'));
const HostSpecialEvents = Loadable(() => import('pages/clubastra/specialEvents'));
const TicketSales = Loadable(() => import('pages/clubastra/ticketSales'));
const ArtistEvent = Loadable(() => import('pages/clubastra/artistEvent'));
const Payments = Loadable(() => import('pages/admin/payments'));
const TicketedEventsAdmin = Loadable(() => import('pages/admin/ticketedEvents'));
const Cashouts = Loadable(() => import('pages/admin/cashouts'));
const Incentives = Loadable(() => import('pages/admin/incentives'));
const Annoucements = Loadable(() => import('pages/admin/annoucements'));
const AppPopupManager = Loadable(() => import('pages/admin/AppPopupManager'));
const HostRequestsAdmin = Loadable(() => import('pages/admin/hostRequests'));
const Sms = Loadable(() => import('pages/admin/sms'));
const SpecialEvents = Loadable(() => import('pages/admin/specialEvents'));
const Birthdays = Loadable(() => import('pages/admin/birthdays'));
const DinnerBookings = Loadable(() => import('pages/admin/dinnerBookings'));
const BoothSettings = Loadable(() => import('pages/admin/BoothSettings'));
const PoshEventMapping = Loadable(() => import('pages/admin/PoshEventMapping'));
const PublicRsvpSettings = Loadable(() => import('pages/admin/PublicRsvpSettings'));
const PublicHostOnboarding = Loadable(() => import('pages/admin/PublicHostOnboarding'));
const BusinessGuestlist = Loadable(() => import('pages/admin/businessGuestlist'));
const BusinessUsageReport = Loadable(() => import('pages/reports/businessUsage'));
const FrontDoorRules = Loadable(() => import('pages/admin/FrontDoorRules'));
const HostPhotoReportEmailSettings = Loadable(() => import('pages/admin/HostPhotoReportEmailSettings'));
const NewBrandSetup = Loadable(() => import('pages/admin/NewBrandSetup'));
const BrandDashboard = Loadable(() => import('pages/admin/BrandDashboard'));
const BrandList = Loadable(() => import('pages/admin/BrandList'));
const EmersonHome = Loadable(() => import('pages/emerson/EmersonHome'));
const EmersonReports = Loadable(() => import('pages/emerson/EmersonReports'));
const YoursTrulyHome = Loadable(() => import('pages/yours-truly/YoursTrulyHome'));
const YoursTrulyReports = Loadable(() => import('pages/yours-truly/YoursTrulyReports'));
const YoursTrulyHosts = Loadable(() => import('pages/yours-truly/YoursTrulyHosts'));
const ManagementFreeListPortal = Loadable(() => import('pages/management/ManagementFreeListPortal'));
const ManagementFreeListReport = Loadable(() => import('pages/management/ManagementFreeListReport'));

const MainRoutes = {
  path: '/',
  element: (
    <RequireAuth>
      <DashboardLayout />
    </RequireAuth>
  ),
  children: [
    { index: true, element: <DashboardDefault /> },
    {
      path: 'dashboard',
      children: [{ path: 'default', element: <DashboardDefault /> }]
    },

    { path: 'typography', element: <Typography /> },
    { path: 'color', element: <Color /> },
    { path: 'shadow', element: <Shadow /> },
    { path: 'sample-page', element: <SamplePage /> },

    { path: 'not-authorized', element: <NotAuthorized /> },

    // Emerson
    {
      path: 'emerson',
      element: (
        <ProtectedRoute>
          <EmersonHome />
        </ProtectedRoute>
      )
    },
    {
      path: 'emerson/reports',
      element: (
        <ProtectedRoute>
          <EmersonReports />
        </ProtectedRoute>
      )
    },

    // Yours Truly
    {
      path: 'yours-truly',
      element: (
        <ProtectedRoute>
          <YoursTrulyHome />
        </ProtectedRoute>
      )
    },
    {
      path: 'yours-truly/hosts',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.YOURS_TRULY]}>
          <YoursTrulyHosts />
        </ProtectedRoute>
      )
    },
    {
      path: 'yours-truly/reports',
      element: (
        <ProtectedRoute>
          <YoursTrulyReports />
        </ProtectedRoute>
      )
    },
    {
      path: 'bar-bambi',
      element: (
        <ProtectedRoute>
          <ManagementFreeListPortal listKey="bar-bambi" />
        </ProtectedRoute>
      )
    },
    {
      path: 'bar-bambi/reports',
      element: (
        <ProtectedRoute>
          <ManagementFreeListReport listKey="bar-bambi" />
        </ProtectedRoute>
      )
    },
    {
      path: 'bar-bambi/host-photo-attendance',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.BAR_BAMBI]}>
          <HostPhotoAttendanceReport />
        </ProtectedRoute>
      )
    },
    {
      path: 'management/free-list',
      element: (
        <ProtectedRoute>
          <ManagementFreeListPortal />
        </ProtectedRoute>
      )
    },
    {
      path: 'management/free-list/:listKey',
      element: (
        <ProtectedRoute>
          <ManagementFreeListPortal />
        </ProtectedRoute>
      )
    },
    {
      path: 'management/free-list/:listKey/reports',
      element: (
        <ProtectedRoute>
          <ManagementFreeListReport />
        </ProtectedRoute>
      )
    },

    // Reports
    {
      path: 'reports',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <ReportMenu />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/me',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]}>
          <MyReports />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/team',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.AMBASSADOR, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]}>
          <TeamReports />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/team-birthday',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.AMBASSADOR, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]}>
          <TeamBirthday />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/night-summary',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <NightSummary />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/public-rsvp',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <PublicRsvpReport />
        </ProtectedRoute>
      )
    },
    {
      // Ticket Sales (owner 2026-10-06): the server decides which events and rows each admin sees.
      path: 'reports/ticket-sales',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <TicketSalesReport />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/host-photo-attendance',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <HostPhotoAttendanceReport />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/business-usage',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <BusinessUsageReport />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/all',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <AllReports />
        </ProtectedRoute>
      )
    },
    {
      path: 'reports/current-team',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.HEAD_HOST, ROLES.AMBASSADOR, ROLES.MANAGEMENT, ROLES.ADMIN]}>
          <CurrentTeam />
        </ProtectedRoute>
      )
    },

    // Host pages (brand-neutral addresses, e.g. /tickets)
    {
      path: 'bday',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <Bday />
        </ProtectedRoute>
      )
    },
    {
      path: 'booths',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <Booths />
        </ProtectedRoute>
      )
    },
    {
      path: 'content',
      element: <Content />
    },
    {
      path: 'perks',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <Perks />
        </ProtectedRoute>
      )
    },
    {
      path: 'pays',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <Pays />
        </ProtectedRoute>
      )
    },
    {
      path: 'missions',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <HostMissions />
        </ProtectedRoute>
      )
    },
    {
      path: 'playbook',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <Playbook />
        </ProtectedRoute>
      )
    },
    {
      // hidden art-review gallery — no nav item, direct URL only
      path: 'plays-gallery',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <PlaysGallery />
        </ProtectedRoute>
      )
    },
    {
      path: 'addGuestlist',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <AddGuestlist />
        </ProtectedRoute>
      )
    },
    {
      path: 'freeentry',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <FreeEntry />
        </ProtectedRoute>
      )
    },
    {
      path: 'managementFreeList',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]} allowedProfileFlags={[ACCESS_FLAGS.MANAGEMENT_FREE_LIST]}>
          <ManagementFreeList />
        </ProtectedRoute>
      )
    },
    {
      path: 'requestHost',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.AMBASSADOR, ROLES.HOST]}>
          <RequestHost />
        </ProtectedRoute>
      )
    },
    {
      path: 'managementRequestHost',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <ManagementRequestHost />
        </ProtectedRoute>
      )
    },
    {
      path: 'addDinner',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <AddDinner />
        </ProtectedRoute>
      )
    },
    {
      path: 'addDinnerManagement',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <AddDinnerManagement />
        </ProtectedRoute>
      )
    },
    {
      path: 'specialEvents',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <HostSpecialEvents />
        </ProtectedRoute>
      )
    },
    {
      path: 'tickets',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <TicketSales />
        </ProtectedRoute>
      )
    },
    // Old hardcoded promo pages: their links now open Promote & Earn.
    {
      path: 'nye',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <TicketSales />
        </ProtectedRoute>
      )
    },
    {
      path: 'publicHols',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <TicketSales />
        </ProtectedRoute>
      )
    },
    {
      path: 'artistEvent',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT, ROLES.HEAD_HOST, ROLES.HOST, ROLES.AMBASSADOR, ROLES.DJ]}>
          <ArtistEvent />
        </ProtectedRoute>
      )
    },

    // Old addresses: host pages used to sit under /clubastra/... (plus unused
    // /manage/... copies), which showed Club Astra's name on every brand's
    // portal. Those links now forward to the brand-neutral page above.
    { path: 'clubastra/*', element: <LegacyHostRedirect /> },
    { path: 'manage/*', element: <LegacyHostRedirect /> },

    // Admin
    {
      path: 'admin/payments',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <Payments />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/cashouts',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <Cashouts />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/incentives',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <Incentives />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/annoucements',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <Annoucements />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/appPopups',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <AppPopupManager />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/host-requests',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <HostRequestsAdmin />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/sms',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <Sms />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/specialEvents',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <SpecialEvents />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/birthdays',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <Birthdays />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/dinnerBookings',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <DinnerBookings />
        </ProtectedRoute>
      )
    },
    {
      // Booth deposits step 1: admins edit, management views (server enforces both).
      path: 'admin/booth-settings',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <BoothSettings />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/public-rsvp',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <PublicRsvpSettings />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/business-guestlist',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <BusinessGuestlist />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/public-host-onboarding',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGEMENT]}>
          <PublicHostOnboarding />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/front-door-rules',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <FrontDoorRules />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/host-photo-report-emails',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <HostPhotoReportEmailSettings />
        </ProtectedRoute>
      )
    },
    {
      path: 'admin/poshEventMapping',
      element: (
        <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
          <PoshEventMapping />
        </ProtectedRoute>
      )
    },

    // Platform (superadmin-only): create + manage brands. Gated by the superadmin Auth claim,
    // NOT a role (ProtectedRoute/allowedRoles would let the `admin` role through). The
    // provisionBrand/activateBrand callables also re-check superadmin server-side. `new` is
    // listed before `:eventId` so it never resolves as a brand id.
    {
      path: 'admin/ticketed-events',
      element: (
        <SuperadminRoute>
          <TicketedEventsAdmin />
        </SuperadminRoute>
      )
    },
    {
      path: 'admin/brands',
      element: (
        <SuperadminRoute>
          <BrandList />
        </SuperadminRoute>
      )
    },
    {
      path: 'admin/brands/new',
      element: (
        <SuperadminRoute>
          <NewBrandSetup />
        </SuperadminRoute>
      )
    },
    {
      path: 'admin/brands/:eventId',
      element: (
        <SuperadminRoute>
          <BrandDashboard />
        </SuperadminRoute>
      )
    }
  ]
};

export default MainRoutes;
